import React, { useState, useEffect, useCallback } from 'react';
import { productService } from '../services/api';
import Footer from '../components/Footer';
import ChangeHistory from '../components/ChangeHistory';
import { useInvoice } from '../context/InvoiceContext';

const formatINR = (n) => '\u20B9' + parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Products = () => {
  const { currentUser } = useInvoice();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', rate: '', gst: 18 });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      const res = await productService.list();
      setProducts(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    if (!selectedProduct) { setHistory([]); setShowHistory(false); return; }
    setHistoryLoading(true);
    productService.history(selectedProduct.id).then(res => setHistory(res.data || [])).catch(() => {}).finally(() => setHistoryLoading(false));
  }, [selectedProduct]);

  const resetForm = () => {
    setForm({ name: '', rate: '', gst: '18' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.rate) return;
    setSaving(true);
    try {
      if (editingId) {
        await productService.update(editingId, form);
      } else {
        await productService.create(form);
      }
      await loadProducts();
      resetForm();
    } catch (e) { console.error('Failed to save product:', e); }
    finally { setSaving(false); }
  };

  const startEdit = (p) => {
    setForm({ name: p.name, rate: String(p.rate), gst: 18 });
    setEditingId(p.id);
    setShowForm(true);
    setSelectedProduct(null);
  };

  const handleDelete = async (id) => {
    try {
      await productService.delete(id);
      await loadProducts();
      if (selectedProduct?.id === id) setSelectedProduct(null);
    } catch (e) { console.error('Failed to delete product:', e); }
  };

  return (
    <>
      <div className="main-content">
        <div className="container">
          <div className="flex-between mb-lg">
            <h1>Products</h1>
            {isSuperAdmin && !showForm && !selectedProduct && (
              <button onClick={() => { resetForm(); setShowForm(true); }} className="btn btn-primary">+ Add Product</button>
            )}
          </div>

          {/* Create/Edit Form */}
          {showForm && (
            <div className="card mb-md">
              <div className="card-header">{editingId ? 'Edit Product' : 'New Product'}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                <div className="form-group">
                  <label className="form-label">Name <span style={{ color: '#DC2626' }}>*</span></label>
                  <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    style={{ borderColor: !form.name.trim() ? '#DC2626' : undefined, boxShadow: !form.name.trim() ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} placeholder="Product name" />
                </div>
                <div className="form-group">
                  <label className="form-label">Rate <span style={{ color: '#DC2626' }}>*</span></label>
                  <input className="form-input" type="number" value={form.rate} onChange={e => setForm(f => ({ ...f, rate: e.target.value }))}
                    style={{ borderColor: !form.rate ? '#DC2626' : undefined, boxShadow: !form.rate ? '0 0 0 2px rgba(220,38,38,0.15)' : undefined }} placeholder="0.00" />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-sm)', marginTop: 'var(--spacing-md)' }}>
                <button onClick={resetForm} className="btn btn-secondary">Cancel</button>
                <button onClick={handleSave} disabled={!form.name.trim() || !form.rate || saving} className="btn btn-primary">{saving ? 'Saving...' : editingId ? 'Save' : 'Create'}</button>
              </div>
            </div>
          )}

          {/* Product Detail */}
          {selectedProduct && !showForm && (
            <>
              <div style={{ marginBottom: 'var(--spacing-md)', display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button onClick={() => setSelectedProduct(null)} className="btn btn-secondary btn-sm">Back to list</button>
                {isSuperAdmin && <button onClick={() => startEdit(selectedProduct)} className="btn btn-secondary btn-sm">Edit</button>}
              </div>
              <div className="card mb-md">
                <div className="card-header">{selectedProduct.name}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                  <div>
                    <div className="form-label">Rate</div>
                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-primary)' }}>{formatINR(selectedProduct.rate)}</div>
                  </div>
                  <div>
                    <div className="form-label">GST</div>
                    <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)' }}>{selectedProduct.gst || 18}%</div>
                  </div>
                </div>
              </div>
              <div className="card mb-md">
                <div className="card-header" style={{ cursor: 'pointer' }} onClick={() => setShowHistory(!showHistory)}>
                  {showHistory ? '▾' : '▸'} History <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>({history.length})</span>
                </div>
                {showHistory && (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <ChangeHistory history={history} loading={historyLoading} />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Product List */}
          {!showForm && !selectedProduct && (
            products.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📦</div>
                <div className="empty-state-text">No products yet</div>
                {isSuperAdmin && <button onClick={() => { resetForm(); setShowForm(true); }} className="btn btn-primary mt-md">Add First Product</button>}
              </div>
            ) : (
              <div className="card" style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'left', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Name</th>
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Rate</th>
                      <th style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-secondary)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(p => (
                      <tr key={p.id} style={{ borderBottom: '1px solid var(--color-gray-100)', cursor: 'pointer' }} onClick={() => setSelectedProduct(p)}>
                        <td style={{ padding: 'var(--spacing-sm)', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)' }}>{p.name}</td>
                        <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right', fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)' }}>{formatINR(p.rate)}</td>
                        <td style={{ padding: 'var(--spacing-sm)', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                          {isSuperAdmin && <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                            <button onClick={() => startEdit(p)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px' }}>Edit</button>
                            <button onClick={() => handleDelete(p.id)} className="btn btn-secondary btn-sm" style={{ fontSize: '11px', padding: '2px 10px', color: '#DC2626' }}>Delete</button>
                          </div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Products;
